"use client";
/**
 * Shared `useRestaurantPools` hook (Mobile P0.8).
 *
 * Loads a restaurant's tip pools and derives the default pool id. RN-compatible:
 * no DOM/Next imports, uses the global `fetch`. The API host is injected via
 * `baseUrl` so web can keep relative URLs while mobile points at its API origin.
 *
 * Marked `"use client"` so Next.js treats it as a client boundary when reached
 * through the `@tippo/data` barrel from a Server Component graph.
 */
import { useEffect, useMemo, useState } from "react";
import { listTipPools } from "../queries/tipPools";
/** Coerces a raw API pools payload into the strict `TRestaurantPool[]` shape. */
export function normalizePools(raw) {
    const list = Array.isArray(raw) ? raw : [];
    return list.map((p) => ({
        id: p.id,
        name: p.name,
        isDefault: Boolean(p.isDefault),
        splitPercent: p.splitPercent === undefined || p.splitPercent === null
            ? null
            : p.splitPercent,
    }));
}
/** Default pool is the one flagged `isDefault`, else the first pool, else null. */
export function getDefaultPoolId(pools) {
    const defaultPool = pools.find((p) => p.isDefault);
    if (defaultPool)
        return defaultPool.id;
    return pools[0]?.id ?? null;
}
/**
 * Client hook that loads a restaurant's tip pools and derives the default pool id.
 * Source of truth: `GET {baseUrl}/api/restaurants/[restaurantId]/tip-pools`.
 */
export function useRestaurantPools(restaurantId, options = {}) {
    const { baseUrl = "", supabase: supabaseClient } = options;
    const [pools, setPools] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    useEffect(() => {
        let cancelled = false;
        if (!restaurantId)
            return;
        setLoading(true);
        setError(null);
        const loadFromSupabase = () => {
            if (!supabaseClient)
                return Promise.resolve();
            return listTipPools(supabaseClient, restaurantId)
                .then((rows) => {
                if (cancelled)
                    return;
                setPools(rows.map((p) => ({
                    id: p.id,
                    name: p.name,
                    isDefault: p.isDefault,
                    splitPercent: p.splitPercent,
                })));
                setError(null);
            })
                .catch(() => {
                if (cancelled)
                    return;
                setError("Failed to load pools");
                setPools([]);
            })
                .then(() => {
                if (cancelled)
                    return;
                setLoading(false);
            });
        };
        const loadFromApi = () => fetch(`${baseUrl}/api/restaurants/${restaurantId}/tip-pools`)
            .then((r) => r.json())
            .then((data) => {
            if (cancelled)
                return;
            const next = normalizePools(data?.pools);
            setPools(next);
            setError(data?.error ? String(data.error) : null);
        })
            .catch(() => {
            if (cancelled)
                return;
            setError("Failed to load pools");
            setPools([]);
        })
            .finally(() => {
            if (cancelled)
                return;
            setLoading(false);
        });
        void (supabaseClient ? loadFromSupabase() : loadFromApi());
        return () => {
            cancelled = true;
        };
    }, [restaurantId, baseUrl, supabaseClient]);
    const defaultPoolId = useMemo(() => getDefaultPoolId(pools), [pools]);
    return { pools, defaultPoolId, loading, error };
}
